// Dashboard page — mirrors the legacy DashboardPage component.
// All widgets rendered inline; single data source from /dashboard/stats
// and /dashboard/health-services, both refreshed every 15s by TanStack Query.

import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  SEVERITY_META,
  SeverityBadge,
} from "@/components/ui/SeverityBadge";
import { Stat } from "@/components/ui/Stat";
import { TrendChart } from "@/components/dashboard/TrendChart";
import {
  useDashboardStats,
  useHealthServices,
  usePlanUsage,
} from "@/hooks/useDashboard";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { ApiError } from "@/lib/api";
import type { Severity } from "@/types/dashboard";
import { getStatusBadgeColors } from "@/types/engagement";
import { friendlyToolName } from "@/lib/toolLabels";

const SEVERITY_ORDER: Severity[] = [
  "critical",
  "high",
  "medium",
  "low",
  "informational",
];

function formatDuration(seconds: number): string {
  if (!seconds) return "0m";
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 100 ? "rgb(var(--color-danger))" : pct >= 80 ? "rgb(var(--color-warning))" : "rgb(var(--color-accent))";
  return (
    <div className="flex-1 min-w-[160px]">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] text-muted uppercase tracking-[0.08em]">
          {label}
        </span>
        <span className="font-mono text-[12px] text-content">
          {used} / {limit}
        </span>
      </div>
      <div className="h-[6px] bg-bg2 rounded overflow-hidden">
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const tt = useDict().app.dashboard;
  const stats = useDashboardStats();
  const health = useHealthServices();
  const usage = usePlanUsage();

  if (stats.isError) {
    const msg =
      stats.error instanceof ApiError
        ? String(stats.error.detail)
        : stats.error instanceof Error
          ? stats.error.message
          : tt.unknownError;
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[13px]">{tt.error} {msg}</div>
      </Card>
    );
  }

  if (!stats.data) return <Loading text={tt.loading} />;

  const { engagements: eng, findings: fnd, executions: ex } = stats.data;

  return (
    <div>
      <PageHeader
        title={tt.title}
        subtitle={tt.subtitle}
      />

      {/* Plan usage — hidden on unlimited tiers (both caps 0). */}
      {usage.data && (usage.data.max_scans_per_month > 0 || usage.data.max_engagements > 0) && (
        <Card className="mb-4">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-shrink-0">
              <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-1">
                {tt.plan}
              </div>
              <div className="font-display text-[14px] font-semibold text-accent">
                {usage.data.plan_name}
              </div>
            </div>
            {usage.data.max_scans_per_month > 0 && (
              <UsageBar
                label={tt.scansThisMonth}
                used={usage.data.scans_this_month}
                limit={usage.data.max_scans_per_month}
              />
            )}
            {usage.data.max_engagements > 0 && (
              <UsageBar
                label={tt.usageEngagements}
                used={usage.data.engagements_total}
                limit={usage.data.max_engagements}
              />
            )}
          </div>
        </Card>
      )}

      {/* Top stat cards — 2-up on mobile, 4-up from sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Card className="cb-grow">
          <Stat
            label={tt.totalEngagements}
            value={eng.total}
            color="rgb(var(--color-accent))"
            sub={fmt(tt.thisWeek, { n: eng.recent_7d })}
          />
        </Card>
        <Card className="cb-grow">
          <Stat
            label={tt.totalFindings}
            value={fnd.total}
            color="rgb(var(--color-content))"
            sub={fmt(tt.criticalPending, { n: fnd.critical_pending_review })}
          />
        </Card>
        <Card className="cb-grow">
          <Stat
            label={tt.evidenceCollected}
            value={stats.data.evidence.total}
            color="rgb(var(--color-info))"
          />
        </Card>
        <Card className="cb-grow">
          <Stat
            label={tt.avgScanDuration}
            value={formatDuration(ex.avg_scan_duration_seconds)}
            color="rgb(var(--color-brand))"
            sub={fmt(tt.successRate, { n: ex.success_rate.toFixed(0) })}
          />
        </Card>
      </div>

      {/* Live scanning + Health row — stacked on mobile, 2fr/1fr from md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3 mb-4">
        {/* Live scanning */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-2 h-2 rounded-full"
              style={{
                background:
                  eng.currently_scanning.length > 0 ? "rgb(var(--color-accent))" : "rgb(var(--color-dim))",
                animation:
                  eng.currently_scanning.length > 0
                    ? "glow 2s infinite"
                    : "none",
              }}
            />
            <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em]">
              {fmt(tt.currentlyScanning, { n: eng.currently_scanning.length })}
            </div>
          </div>
          {eng.currently_scanning.length === 0 ? (
            <div className="p-6 text-center text-dim font-mono text-[12px]">
              {tt.noScans}
            </div>
          ) : (
            eng.currently_scanning.map((s) => (
              <div
                key={s.id}
                onClick={() => navigate(`/app/engagements/${s.id}`)}
                className="px-3 py-[10px] bg-bg2 rounded mb-2 cursor-pointer border border-accent/25 flex justify-between items-center"
              >
                <div>
                  <div className="font-sans text-[13px] font-semibold text-content">
                    {s.name}
                  </div>
                  <div className="font-mono text-[10px] text-muted mt-[2px]">
                    {s.pentest_type} · {tt.started}{" "}
                    {s.started_at
                      ? new Date(s.started_at).toLocaleTimeString()
                      : tt.recently}
                  </div>
                </div>
                <Badge color="rgb(var(--color-accent))" bg="rgb(var(--color-accent-bg))">
                  {tt.live}
                </Badge>
              </div>
            ))
          )}
        </Card>

        {/* Service health */}
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.serviceHealth}
          </div>
          {health.data ? (
            health.data.services.map((s) => (
              <div
                key={s.name}
                className="flex items-center gap-2 py-[6px] border-b border-stroke"
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{
                    background:
                      s.color === "green"
                        ? "rgb(var(--color-success))"
                        : s.color === "yellow"
                          ? "rgb(var(--color-warning))"
                          : "rgb(var(--color-danger))",
                  }}
                />
                <div className="flex-1">
                  <div className="font-mono text-[11px] text-content">
                    {s.name}
                  </div>
                  <div className="font-mono text-[9px] text-muted">
                    {s.details}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <Loading text="..." />
          )}
        </Card>
      </div>

      {/* Severity + Pentest types — stacked on mobile, 2-up from sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.findingsBySeverity}
          </div>
          <div className="flex gap-3 justify-around">
            {SEVERITY_ORDER.map((sev) => {
              const v = fnd.by_severity[sev] ?? 0;
              const meta = SEVERITY_META[sev];
              return (
                <div key={sev} className="text-center flex-1">
                  <div
                    className="text-[24px] font-bold font-display"
                    style={{ color: v > 0 ? meta.c : "rgb(var(--color-dim))" }}
                  >
                    {v}
                  </div>
                  <div className="text-[9px] font-mono text-muted uppercase mt-1">
                    {meta.l}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.pentestTypes}
          </div>
          {Object.keys(eng.by_pentest_type).length === 0 ? (
            <div className="text-dim font-mono text-[11px] text-center p-3">
              {tt.noData}
            </div>
          ) : (
            Object.entries(eng.by_pentest_type)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => {
                const max = Math.max(...Object.values(eng.by_pentest_type));
                const pct = (count / max) * 100;
                return (
                  <div key={type} className="mb-2">
                    <div className="flex justify-between font-mono text-[11px] mb-[3px]">
                      <span className="text-content">
                        {type.replace("_", " ")}
                      </span>
                      <span className="text-muted">{count}</span>
                    </div>
                    <div className="h-1 bg-bg2 rounded-[2px] overflow-hidden">
                      <div
                        className="h-full bg-accent"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
          )}
        </Card>
      </div>

      {/* Trend chart */}
      <Card className="mb-4">
        <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
          {tt.findingsTrend}
        </div>
        <TrendChart data={fnd.trend_14d} />
      </Card>

      {/* Critical pending + Recent engagements — stacked on mobile, 2-up from sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Card>
          <div className="font-mono text-[11px] text-danger uppercase tracking-[0.08em] mb-3">
            {tt.criticalFindings}
          </div>
          {fnd.critical_list.length === 0 ? (
            <div className="text-dim font-mono text-[11px] text-center p-3">
              {tt.noneGreat}
            </div>
          ) : (
            fnd.critical_list.map((f) => (
              <div
                key={f.id}
                onClick={() => navigate(`/app/engagements/${f.engagement_id}`)}
                className="flex items-center gap-2 py-[6px] border-b border-stroke cursor-pointer"
              >
                <SeverityBadge severity={String(f.severity)} />
                <span className="font-mono text-[11px] text-content flex-1">
                  {f.title.length > 50 ? f.title.substring(0, 50) + "..." : f.title}
                </span>
              </div>
            ))
          )}
        </Card>
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.recentEngagements}
          </div>
          {eng.recent_list.length === 0 ? (
            <div className="text-dim font-mono text-[11px] text-center p-3">
              {tt.noEngagements}
            </div>
          ) : (
            eng.recent_list.map((e) => {
              const badge = getStatusBadgeColors(e.status);
              return (
                <div
                  key={e.id}
                  onClick={() => navigate(`/app/engagements/${e.id}`)}
                  className="flex items-center gap-2 py-[6px] border-b border-stroke cursor-pointer"
                >
                  <Badge color="rgb(var(--color-accent))" bg="rgb(var(--color-accent-bg))">
                    {e.pentest_type}
                  </Badge>
                  <span className="font-mono text-[11px] text-content flex-1">
                    {e.name.length > 30 ? e.name.substring(0, 30) + "..." : e.name}
                  </span>
                  <Badge color={badge.color} bg={badge.bg}>
                    {e.status}
                  </Badge>
                </div>
              );
            })
          )}
        </Card>
      </div>

      {/* Top tools + Execution stats — stacked on mobile, 2-up from sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.topTools}
          </div>
          {ex.top_tools.length === 0 ? (
            <div className="text-dim font-mono text-[11px] text-center p-3">
              {tt.noData}
            </div>
          ) : (
            ex.top_tools.map((t) => {
              const max = ex.top_tools[0].count;
              const pct = (t.count / max) * 100;
              return (
                <div key={t.tool} className="mb-2">
                  <div className="flex justify-between font-mono text-[11px] mb-[3px]">
                    <span className="text-content">{friendlyToolName(t.tool)}</span>
                    <span className="text-muted">{fmt(tt.runs, { n: t.count })}</span>
                  </div>
                  <div className="h-1 bg-bg2 rounded-[2px] overflow-hidden">
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </Card>
        <Card>
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
            {tt.executionStats}
          </div>
          <div className="flex flex-col gap-[10px]">
            <div className="flex justify-between font-mono text-[12px]">
              <span className="text-muted">{tt.totalExecutions}</span>
              <span className="text-content font-semibold">{ex.total}</span>
            </div>
            <div className="flex justify-between font-mono text-[12px]">
              <span className="text-muted">{tt.successful}</span>
              <span className="text-success font-semibold">
                {ex.success_count}
              </span>
            </div>
            <div className="flex justify-between font-mono text-[12px]">
              <span className="text-muted">{tt.errorsTimeouts}</span>
              <span className="text-danger font-semibold">
                {ex.error_count}
              </span>
            </div>
            <div className="flex justify-between font-mono text-[12px]">
              <span className="text-muted">{tt.successRateLabel}</span>
              <span className="text-content font-semibold">
                {ex.success_rate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between font-mono text-[12px]">
              <span className="text-muted">{tt.errorRate}</span>
              <span
                className="font-semibold"
                style={{ color: ex.error_rate > 20 ? "rgb(var(--color-danger))" : "rgb(var(--color-content))" }}
              >
                {ex.error_rate.toFixed(1)}%
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
